import type { GetServerSideProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { Level, SessionMode } from '@/prakrit/components/practice/practice-types'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { PracticeSession, SessionHeading } from '@/prakrit/components/practice/practice-session'
import { SetPicker } from '@/prakrit/components/practice/set-picker'
import { LEVEL_INFO, MIXED_INFO, modeInfo } from '@/prakrit/components/practice/practice-catalog'
import { isLevel, isSessionMode } from '@/prakrit/components/practice/practice-types'

import {
    TOPIC_BY_ID,
    parseTopics,
    parseVikalp,
    topicsForMode,
    usesVikalp
} from '@/prakrit/components/practice/practice-topics'

interface PracticeModePageProps {
    mode: SessionMode
    level: Level
    /** ?root= or ?stem= from a Word Explorer or Grammar Lab link. */
    focus: string | null
    /** ?topic= : the chosen topics this game understands (Today's practice takes none). */
    topics: string[]
    /** ?vikalp=1 : variant forms included (only for the games that read it). */
    vikalp: boolean
    /** ?set=7 : practice set ७ of the game's bank; without it (and without a focus) the page lists every set. */
    set: number | null
}

const PracticeModePage: FC<PracticeModePageProps> = ({ mode, level, focus, topics, vikalp, set }): ReactNode => {
    const info = mode === 'aaj' ? MIXED_INFO : modeInfo(mode)
    const topicLine = topics.length > 0 ? ` Topics: ${topics.map(id => TOPIC_BY_ID[id]?.full.en ?? id).join(', ')}.` : ''
    const vikalpLine = vikalp ? ' Variant forms included.' : ''

    if (mode !== 'aaj' && !focus && set === null) {
        return (
            <Layout>
                <Seo
                    title={`${info.name.hi} · ${LEVEL_INFO[level].name.hi}`}
                    description={`${info.name.en} (${LEVEL_INFO[level].name.en}): ${info.line.en}${topicLine}${vikalpLine} Up to 500 exercises in numbered sets of ten, graded by the rules of Prakrit grammar.`}
                />
                <SetPicker mode={mode} level={level} topics={topics} vikalp={vikalp} />
            </Layout>
        )
    }

    return (
        <Layout footer={false}>
            <Seo
                title={`${info.name.hi} · ${LEVEL_INFO[level].name.hi}`}
                description={`${info.name.en} (${LEVEL_INFO[level].name.en}): ${info.line.en}${topicLine}${vikalpLine} Ten questions, graded by the rules of Prakrit grammar, with Hindi and English feedback.`}
            />
            <SessionHeading mode={mode} level={level} />
            {/* Remount on a new mode, level, focus, topic or विकल्प choice so a session never carries over. */}
            <PracticeSession
                key={`${mode}|${level}|${focus ?? ''}|${topics.join(',')}|${vikalp ? 'v' : ''}|${set ?? ''}`}
                mode={mode}
                level={level}
                focus={focus}
                topics={topics}
                vikalp={vikalp}
                set={mode === 'aaj' || focus ? null : set}
            />
        </Layout>
    )
}

const first = (value: string | string[] | undefined): string => (Array.isArray(value) ? value[0] ?? '' : value ?? '')

export const getServerSideProps: GetServerSideProps<PracticeModePageProps> = async ({ params, query }) => {
    const mode = first(params?.mode)

    if (!isSessionMode(mode)) return { notFound: true }

    const wanted = first(query.level)
    const level: Level = isLevel(wanted) ? wanted : 'arambh'
    const focus = (first(query.root) || first(query.stem) || first(query.focus)).normalize('NFC').trim().slice(0, 60) || null
    const topics = mode === 'aaj' ? [] : topicsForMode(mode, parseTopics(query.topic))
    const vikalp = usesVikalp(mode) && parseVikalp(query.vikalp)
    const setNumber = Number.parseInt(first(query.set), 10)
    const set = Number.isInteger(setNumber) && setNumber > 0 && setNumber <= 100 ? setNumber : null

    return { props: { mode, level, focus, topics, vikalp, set } }
}

export default PracticeModePage
